import { useCallback, useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/icons';
import { Alert, Button, Modal, Spinner, Tooltip } from '@/components/ui';
import { ApiError } from '@/api/client';
import {
  assistApi,
  type AssistantCapability,
  type AssistConversation,
} from '@/api/assist';

/**
 * CampusOne Assist, in the app rather than in an iframe.
 *
 * WHY THIS IS NO LONGER AN IFRAME. The frame was pointed at
 * `${VITE_ASSIST_URL}/assist?product=campusone-app` and carried no credential of
 * any kind. This browser holds no token by design — identity is a BFF and the
 * session is a cookie for the accounts origin — so nothing inside a frame on the
 * assist origin could authenticate, and the assist API rejects anonymous calls.
 * The launcher opened a panel that could not have worked.
 *
 * Passing a token through the frame URL was the alternative and is worse: it puts
 * a bearer token in a URL, where it lands in history, in the referrer, and in
 * every access log along the way. So the panel is rendered here and its calls go
 * through the same BFF proxy as everything else in this app — the proxy attaches
 * an assist-scoped token and signs the institution headers assist verifies.
 *
 * WHICH ASSISTANTS APPEAR IS THE SERVER'S ANSWER, not this component's. A
 * guardian is offered the parent assistant; a bursar is offered the staff ones. No
 * role logic lives here, so there is nothing to drift out of step with the rules
 * that are actually enforced.
 */

/** Kept for callers that still ask whether Assist is configured. */
export function assistOrigin(): string | undefined {
  const url = import.meta.env.VITE_ASSIST_URL?.trim();
  return url || undefined;
}

type AssistModalProps = {
  open: boolean;
  onClose: () => void;
};

interface Exchange {
  id: string;
  question: string;
  answer: string | null;
  failed?: string;
}

export function AssistModal({ open, onClose }: AssistModalProps) {
  const [assistants, setAssistants] = useState<AssistantCapability[] | null>(null);
  const [selected, setSelected] = useState<string>('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [question, setQuestion] = useState('');
  const [pending, setPending] = useState(false);
  const [exchanges, setExchanges] = useState<Exchange[]>([]);
  const transcriptRef = useRef<HTMLDivElement>(null);

  // Asked for on open, not on mount: the panel lives in the app shell on every
  // page, and requesting the catalogue for everyone who never opens it is a call
  // per page load for nothing.
  useEffect(() => {
    if (!open || assistants !== null) return;
    let cancelled = false;
    assistApi
      .capabilities()
      .then((list) => {
        if (cancelled) return;
        setAssistants(list);
        setSelected((current) => current || (list[0]?.code ?? ''));
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setAssistants([]);
        setLoadError(describe(error));
      });
    return () => {
      cancelled = true;
    };
  }, [open, assistants]);

  useEffect(() => {
    transcriptRef.current?.scrollTo({
      top: transcriptRef.current.scrollHeight,
      behavior: 'smooth',
    });
  }, [exchanges]);

  const send = useCallback(async () => {
    const text = question.trim();
    if (!text || !selected || pending) return;

    // The question is shown immediately with a pending answer. A model call takes
    // seconds, and a composer that empties into nothing reads as a lost message.
    const id = `${Date.now()}`;
    setExchanges((prior) => [...prior, { id, question: text, answer: null }]);
    setQuestion('');
    setPending(true);

    try {
      const answered: AssistConversation = await assistApi.ask(selected, text);
      setExchanges((prior) =>
        prior.map((item) =>
          item.id === id ? { ...item, answer: answered.answer } : item,
        ),
      );
    } catch (error: unknown) {
      setExchanges((prior) =>
        prior.map((item) =>
          item.id === id ? { ...item, failed: describe(error) } : item,
        ),
      );
    } finally {
      setPending(false);
    }
  }, [question, selected, pending]);

  const current = assistants?.find((a) => a.code === selected);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="CampusOne Assist"
      className="modal--assist"
      bodyClassName="assist-modalBody"
    >
      <div className="flex h-full flex-col gap-3">
        {loadError && <Alert tone="error">{loadError}</Alert>}

        {assistants === null && (
          <div className="flex items-center gap-2 text-sm text-muted">
            <Spinner /> Loading your assistants…
          </div>
        )}

        {/*
          An empty catalogue is a real answer, not a failure: this person holds no
          role that entitles them to an assistant. Saying so is more useful than an
          empty dropdown that looks broken.
        */}
        {assistants !== null && assistants.length === 0 && !loadError && (
          <Alert tone="info">
            Your account does not currently have access to any assistants. If you
            think that is wrong, ask your institution administrator to check your
            role.
          </Alert>
        )}

        {assistants !== null && assistants.length > 0 && (
          <>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium">Assistant</span>
              <select
                className="input"
                value={selected}
                onChange={(event) => setSelected(event.target.value)}
                disabled={pending}
              >
                {assistants.map((assistant) => (
                  <option key={assistant.code} value={assistant.code}>
                    {assistant.name}
                  </option>
                ))}
              </select>
              {current && (
                <span className="text-xs text-muted">{current.description}</span>
              )}
            </label>

            <div
              ref={transcriptRef}
              className="assist-transcript flex-1 overflow-y-auto rounded-md border border-subtle p-3"
              aria-live="polite"
            >
              {exchanges.length === 0 && (
                <p className="text-sm text-muted">
                  Ask {current?.name ?? 'your assistant'} a question to begin.
                </p>
              )}
              {exchanges.map((exchange) => (
                <div key={exchange.id} className="mb-4 last:mb-0">
                  <p className="assist-question font-medium">{exchange.question}</p>
                  {exchange.failed ? (
                    <Alert tone="error">{exchange.failed}</Alert>
                  ) : exchange.answer === null ? (
                    <span className="flex items-center gap-2 text-sm text-muted">
                      <Spinner /> Thinking…
                    </span>
                  ) : (
                    <p className="assist-answer whitespace-pre-wrap">
                      {exchange.answer}
                    </p>
                  )}
                </div>
              ))}
            </div>

            <form
              className="flex items-end gap-2"
              onSubmit={(event) => {
                event.preventDefault();
                void send();
              }}
            >
              <textarea
                className="input flex-1"
                rows={2}
                value={question}
                placeholder="Type your question…"
                disabled={pending}
                onChange={(event) => setQuestion(event.target.value)}
                onKeyDown={(event) => {
                  // Enter sends, Shift+Enter breaks the line — the convention
                  // every chat surface uses, so anything else feels broken.
                  if (event.key === 'Enter' && !event.shiftKey) {
                    event.preventDefault();
                    void send();
                  }
                }}
              />
              <Button type="submit" disabled={pending || !question.trim()}>
                Send
              </Button>
            </form>
          </>
        )}
      </div>
    </Modal>
  );
}

/**
 * Turns a failure into something the reader can act on.
 *
 * The two the API actually returns are worth separating. A 403 here is almost
 * always a missing institution context rather than a missing right, and telling
 * someone "not allowed" when the fix is "pick an institution" sends them to the
 * wrong person.
 */
function describe(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.isForbidden) {
      return (
        error.message ||
        'You are not able to use this assistant in your current institution.'
      );
    }
    if (error.isMissingEndpoint) {
      return 'CampusOne Assist is not available on this environment yet.';
    }
    if (error.isUnreachable) {
      return 'Could not reach CampusOne Assist. Check your connection and try again.';
    }
    return error.message;
  }
  return 'Something went wrong talking to CampusOne Assist.';
}

/** Opens the Assist panel. */
export function useAssistLauncher(): {
  available: boolean;
  open: boolean;
  openAssist: () => void;
  closeAssist: () => void;
} {
  const [open, setOpen] = useState(false);
  const openAssist = useCallback(() => setOpen(true), []);
  const closeAssist = useCallback(() => setOpen(false), []);
  // Always available now. It used to depend on VITE_ASSIST_URL because the panel
  // was a frame that needed an origin to point at; the panel is part of this app
  // and reaches assist through the same BFF as every other call, so there is no
  // build-time URL for it to be missing.
  return { available: true, open, openAssist, closeAssist };
}

type AssistFabProps = {
  showFab?: boolean;
};

/** Fixed bottom-right Assist button. */
export function AssistFab({ showFab = true }: AssistFabProps) {
  const { open, openAssist, closeAssist } = useAssistLauncher();

  return (
    <>
      {showFab && !open && (
        <Tooltip content="CampusOne Assist">
          <button
            type="button"
            className="assist-fab fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-brand text-white shadow-card transition-colors duration-fast hover:bg-brand-6"
            onClick={openAssist}
            aria-label="CampusOne Assist"
          >
            <Icon name="sparkles" size={24} aria-hidden="true" />
          </button>
        </Tooltip>
      )}
      <AssistModal open={open} onClose={closeAssist} />
    </>
  );
}

/** Modal only — for pages that launch Assist from their own button. */
export function AssistLauncherHost({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}) {
  return <AssistModal open={open} onClose={onClose} />;
}
